import React, { useState } from 'react';
import { Star, ArrowRight } from 'lucide-react';
type Testimonial = {
  name: string;
  city: string;
  title: string;
  text: string;
  before: string;
  after: string;
  time: string;
  img?: string;
  isUserSubmitted?: boolean;
};
const initialTestimonials: Testimonial[] = [{
  title: "I felt like I was 'allergic' to good news.",
  name: 'Amanda S.',
  city: 'Seattle, WA',
  text: "For three months, everything I touched fell apart. My car's transmission died, my landlord raised the rent, and I was passed over for a promotion I was promised. I told my sister I felt like I had a 'black cloud' over my head. I bought the 417hz track at 2 AM on a Tuesday because I was crying and desperate. By Day 4 of listening, the 'heavy' feeling in my chest just... evaporated. The next day, a headhunter messaged me on LinkedIn for a role that pays 20% more. This isn't just audio; it's a life-reset.",
  before: 'Constant bad luck streak',
  after: 'New job offer & peace',
  time: '5 Days',
  img: 'https://cdn.magicpatterns.com/uploads/vucUnZDnZXyiXrV5Z7bsZo/c1339fb5-b981-4080-853b-0016302b2fb1.png'
}, {
  title: 'I stopped attracting the wrong energy.',
  name: 'Jessica R.',
  city: 'Miami, FL',
  text: "I've spent thousands on crystals and 'cleansing' retreats, but I kept attracting the same toxic patterns in my life. Serena's approach was different—she talked about 'static interference.' I realized my frequency was literally tuned to the wrong station. I started the 7-day protocol, and the shift in my household was immediate. Even my kids seemed calmer. Two weeks later, I met someone who is actually consistent and kind. I finally feel 'visible' to the right people.",
  before: 'Toxic attraction patterns',
  after: 'High-value relationship & home peace',
  time: '12 Days',
  img: 'https://cdn.magicpatterns.com/uploads/ruCuMTK3ND96PsAc4Cuns2/94db5963-c261-476f-8cf0-96f32ea9fabd.png'
}, {
  title: 'The 55-second bonus saved my sanity.',
  name: 'Maria G.',
  city: 'San Diego, CA',
  text: "The main audio is great, but the 'Aftershock Reset' bonus is my secret weapon. I used to get post-argument anxiety that would ruin my whole day. Last week, after a huge blow-up with my boss, I used the 55-second sequence in the bathroom. My heart rate dropped instantly, and the 'doom-loop' in my head stopped. I walked back in with so much clarity that he actually apologized to ME. It feels like having a remote control for my luck.",
  before: 'Chronic post-conflict anxiety',
  after: 'Instant emotional control',
  time: '55 Seconds',
  img: 'https://cdn.magicpatterns.com/uploads/jhPf6YiswtEWM8YcCjfbKE/4e0d5072-def6-4e6b-8169-9edfd6510489.png'
}, {
  title: "Finally, the 'static' is gone.",
  name: 'Sarah P.',
  city: 'New York, NY',
  text: "I'm a freelancer, and I had been in a 'dry spell' for 2 months. Every lead went cold. I realized my energy was screams 'desperation,' and it was blocking everything. I did the Lucky Flush protocol and focused on the 'Energy Leak Finder' guide. I found a massive leak in my digital environment. I cleared it, listened to the track, and within 48 hours, a client I hadn't heard from in a year reached out with a $3,500 project. The static is gone, and the flow is back.",
  before: '2-month financial dry spell',
  after: '$3,500 unexpected project',
  time: '48 Hours',
  img: 'https://cdn.magicpatterns.com/uploads/iL4jgQGNmfqjE8DyhgSUDT/e382ea0c-9213-43ab-8991-02503cfc5475.png'
}];
export function TestimonialCarousel() {
  const [testimonials, setTestimonials] = useState<Testimonial[]>(initialTestimonials);
  // Form state
  const [reviewName, setReviewName] = useState('');
  const [reviewText, setReviewText] = useState('');
  const [reviewRating, setReviewRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const handleSubmitReview = (e: React.FormEvent) => {
    e.preventDefault();
    if (!reviewName.trim() || !reviewText.trim()) return;
    const newReview: Testimonial = {
      name: reviewName.trim(),
      city: 'Verified Buyer',
      title: 'My Experience',
      text: reviewText.trim(),
      before: 'Interference',
      after: 'Clarity',
      time: 'Recently',
      isUserSubmitted: true
    };
    setTestimonials([newReview, ...testimonials]);
    setReviewName('');
    setReviewText('');
    setReviewRating(5);
  };
  const renderStars = (rating: number) => {
    return <div className="flex space-x-1 mb-3" data-id="element-277">
        {[1, 2, 3, 4, 5].map(star => {
        if (rating >= star) {
          return <Star key={star} className="w-4 h-4 fill-gold text-gold" data-id="element-278" />;
        } else if (rating >= star - 0.5) {
          return <div key={star} className="relative w-4 h-4" data-id="element-279">
                <Star className="absolute inset-0 w-4 h-4 text-gold/30" data-id="element-280" />
                <div className="absolute inset-0 overflow-hidden w-[50%]" data-id="element-281">
                  <Star className="w-4 h-4 fill-gold text-gold" data-id="element-282" />
                </div>
              </div>;
        } else {
          return <Star key={star} className="w-4 h-4 text-gold/30 fill-transparent" data-id="element-283" />;
        }
      })}
      </div>;
  };
  const getInitialColor = (name: string) => {
    const colors = ['bg-rose', 'bg-gold', 'bg-burgundy', 'bg-blue-600', 'bg-emerald-600', 'bg-purple-600'];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };
  return <section className="w-full py-14 bg-navy overflow-hidden" data-id="element-284">
      <div className="max-w-5xl mx-auto px-4 flex flex-col items-center" data-id="element-285">
        <p className="text-gold text-sm tracking-[0.2em] uppercase mb-6 font-medium text-center" data-id="element-286">
          WHAT OTHERS ARE SAYING
        </p>

        <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-cream text-center mb-16" data-id="element-287">
          Real Shifts. Real Results.
        </h2>

        {/* Static Testimonials Grid/List */}
        <div className="w-full flex flex-col space-y-8 mb-24" data-id="element-288">
          {testimonials.map((t, idx) => <div key={idx} className="w-full bg-navy-dark border border-gold/30 rounded-2xl p-6 md:p-10 shadow-xl flex flex-col md:flex-row gap-6 md:gap-10" data-id="element-289">
              {/* Left Column: User Info */}
              <div className="flex flex-col items-start md:w-1/4 flex-shrink-0" data-id="element-290">
                <div className="flex items-center space-x-4 md:space-x-0 md:flex-col md:items-start mb-4 md:mb-0" data-id="element-291">
                  {t.isUserSubmitted ? <div className={`w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center text-white font-serif text-3xl md:mb-4 ${getInitialColor(t.name)}`} data-id="element-292">
                      {t.name.charAt(0).toUpperCase()}
                    </div> : <img src={t.img} alt={t.name} className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover border-2 border-gold/30 md:mb-4" data-id="element-293" />}
                  <div className="mt-1" data-id="element-294">
                    <p className="text-gold font-bold text-lg leading-tight" data-id="element-295">
                      {t.name}
                    </p>
                    <div className="flex items-center space-x-1.5 mt-0.5" data-id="element-296">
                      <svg className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor" data-id="element-297">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" data-id="element-298" />
                      </svg>
                      <p className="text-cream/60 text-xs" data-id="element-299">
                        @
                        {t.name.toLowerCase().replace(' ', '_').replace('.', '')}
                      </p>
                    </div>
                    <p className="text-cream/40 text-xs mt-0.5" data-id="element-300">{t.city}</p>
                  </div>
                </div>
              </div>

              {/* Right Column: Review Content */}
              <div className="flex-1 flex flex-col" data-id="element-301">
                {renderStars(5)}
                <h3 className="font-serif text-xl md:text-2xl text-cream mb-4" data-id="element-302">
                  "{t.title}"
                </h3>
                <p className="text-cream/80 italic leading-relaxed mb-8 text-lg" data-id="element-303">
                  "{t.text}"
                </p>

                {/* Before/After Box */}
                <div className="bg-navy border border-white/5 rounded-xl p-4 md:p-5 mt-auto" data-id="element-304">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-2 text-sm" data-id="element-305">
                    <div className="flex flex-col" data-id="element-306">
                      <span className="text-cream/40 uppercase tracking-wider text-[10px] font-bold mb-1" data-id="element-307">
                        Before
                      </span>
                      <span className="text-rose font-medium" data-id="element-308">{t.before}</span>
                    </div>
                    <div className="hidden md:flex items-center justify-center" data-id="element-309">
                      <ArrowRight className="w-5 h-5 text-gold/30" data-id="element-310" />
                    </div>
                    <div className="flex flex-col" data-id="element-311">
                      <span className="text-cream/40 uppercase tracking-wider text-[10px] font-bold mb-1" data-id="element-312">
                        After
                      </span>
                      <span className="text-gold font-medium" data-id="element-313">{t.after}</span>
                    </div>
                    <div className="flex flex-col md:border-l md:border-white/10 md:pl-4 mt-2 md:mt-0 pt-2 md:pt-0 border-t border-white/10 md:border-t-0" data-id="element-314">
                      <span className="text-cream/40 uppercase tracking-wider text-[10px] font-bold mb-1" data-id="element-315">
                        Time to Result
                      </span>
                      <span className="text-cream font-bold" data-id="element-316">{t.time}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>)}
        </div>

        {/* Review Submission Form */}
        <div className="w-full max-w-2xl bg-navy-dark border border-gold/20 rounded-2xl p-8 md:p-10 shadow-2xl" data-id="element-317">
          <h3 className="font-serif text-2xl md:text-3xl text-cream text-center mb-8" data-id="element-318">
            Share Your Experience
          </h3>

          <form onSubmit={handleSubmitReview} className="flex flex-col space-y-6" data-id="element-319">
            <div data-id="element-320">
              <label htmlFor="name" className="block text-sm font-medium text-cream/70 mb-2" data-id="element-321">
                Your Name
              </label>
              <input type="text" id="name" value={reviewName} onChange={e => setReviewName(e.target.value)} placeholder="Your name" required className="w-full bg-navy border border-gold/30 rounded-lg px-4 py-3 text-cream placeholder-cream/30 focus:outline-none focus:border-gold transition-colors" data-id="element-322" />
            </div>

            <div data-id="element-323">
              <label className="block text-sm font-medium text-cream/70 mb-2" data-id="element-324">
                Your Rating
              </label>
              <div className="flex space-x-2" data-id="element-325">
                {[1, 2, 3, 4, 5].map(star => <button key={star} type="button" onClick={() => setReviewRating(star)} onMouseEnter={() => setHoverRating(star)} onMouseLeave={() => setHoverRating(0)} className="focus:outline-none" data-id="element-326">
                    <Star className={`w-8 h-8 transition-colors ${(hoverRating || reviewRating) >= star ? 'fill-gold text-gold' : 'text-gold/30 fill-transparent'}`} data-id="element-327" />
                  </button>)}
              </div>
            </div>

            <div data-id="element-328">
              <label htmlFor="review" className="block text-sm font-medium text-cream/70 mb-2" data-id="element-329">
                Your Review
              </label>
              <textarea id="review" value={reviewText} onChange={e => setReviewText(e.target.value)} placeholder="Tell us about your experience..." required rows={4} className="w-full bg-navy border border-gold/30 rounded-lg px-4 py-3 text-cream placeholder-cream/30 focus:outline-none focus:border-gold transition-colors resize-none" data-id="element-330" />
            </div>

            <button type="submit" className="w-full py-4 bg-cta-red text-white font-bold text-lg rounded-full animate-heartbeat hover:bg-red-700 transition-colors duration-300 mt-4" data-id="element-331">
              Submit My Review
            </button>
          </form>
        </div>
      </div>
    </section>;
}